import { useContext, useState } from 'react';
import {
	Drawer,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	IconButton,
	Typography,
	Box,
	Collapse
} from '@mui/material';
import { People, Work, Description, AccountCircle, Schedule, Settings, Logout, FileCopy } from '@mui/icons-material';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import ListIcon from '@mui/icons-material/List';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { default as axios } from '../axiosConfig';
import UserContext from '../Contexts/UserContext';
import { API_UserLogout } from '../Assets/ApiUrlContainer';

const Sidebar = () => {
	const { user, setUser } = useContext(UserContext);
	// console.log("Sidebar.js - user object:", user);
	const location = useLocation();
	const navigate = useNavigate();
	const [open, setOpen] = useState(true);
	const [expandMenu, setExpandMenu] = useState(false);
	const shouldRender = (allowedRoles) => allowedRoles.includes(user?.RoleId);

	const toggleDrawer = () => {
		setOpen(!open);
	};

	const handleMenuClick = () => {
		setExpandMenu(!expandMenu);
	};

	const getItemStyle = (path) => ({
		color: location.pathname.startsWith(path) ? 'var(--deepPurple-700, #512DA8)' : 'rgba(0, 0, 0, 0.87)',
		fontFamily: 'Roboto',
		fontSize: '16px',
		fontWeight: 400,
		lineHeight: '24px',
		letterSpacing: '0.15px',
		backgroundColor: location.pathname.startsWith(path) ? 'var(--deepPurple-50, #EDE7F6)' : 'transparent',
		borderRadius: '8px',
		padding: '10px',
	});

	const getIconStyle = (path) => ({
		color: location.pathname.startsWith(path) ? 'var(--deepPurple-700, #512DA8)' : 'rgba(0, 0, 0, 0.56)',
		fontSize: '24px',
		fontWeight: 400,
	});

	// Logout handler
	const handleLogout = async () => {
		try {
			await axios.post(API_UserLogout);
			setUser(null);
			navigate('/');
		} catch (error) {
			console.error('Error during logout:', error);
		}
	};

	const goToHomePage = () => navigate('/');

	return (
		user && (
			<div className='sidebarContainer'>
				<Drawer
					variant='permanent'
					className={`${open ? 'sideBarDrawerOpen' : 'sideBarDrawerClosed'}`}
					sx={{
						width: open ? 240 : 60,
						flexShrink: 0,
						'& .MuiDrawer-paper': {
							width: open ? 240 : 60,
							transition: 'width 0.3s',
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between',
						},
					}}
				>
					{/* Top Section */}
					<Box>
						<Box
							display='flex'
							alignItems='center'
							justifyContent='space-between'
							padding='10px'
							sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}
						>
							{open && (
								<Typography
									variant='h6'
									sx={{
										paddingLeft: '10px',
										flexGrow: 1,
										color: 'rgba(0, 0, 0, 0.87)',
										fontFamily: 'Roboto',
										fontSize: '16px',
										fontWeight: 400,
										lineHeight: '24px',
										letterSpacing: '0.15px',
										cursor: 'pointer',
									}}
									onClick={goToHomePage}
								>
									The Growth Spark
								</Typography>
							)}
							<IconButton
								onClick={toggleDrawer}
								sx={{
									transform: open ? 'rotate(0deg)' : 'rotate(180deg)',
									transition: 'transform 0.3s',
								}}
							>
								<MenuOpenIcon style={{ fontSize: 24, color: 'rgba(0, 0, 0, 0.56)' }} />
							</IconButton>
						</Box>

						<List>
							<ListItem
								button
								component={Link}
								to='/jobs'
								selected={location.pathname === '/jobs'}
								sx={getItemStyle('/jobs')}
							>
								<ListItemIcon>
									<Work style={getIconStyle('/jobs')} />
								</ListItemIcon>
								{open && (
									<ListItemText
										primary='Jobs'
										primaryTypographyProps={{ sx: getItemStyle('/jobs') }}
									/>
								)}
							</ListItem>

							{!shouldRender([2]) && (
								<ListItem
									button
									component={Link}
									to='/candidates'
									selected={location.pathname === '/candidates'}
									sx={getItemStyle('/candidates')}
								>
									<ListItemIcon>
										<People style={getIconStyle('/candidates')} />
									</ListItemIcon>
									{open && (
										<ListItemText
											primary='Applicants'
											primaryTypographyProps={{ sx: getItemStyle('/candidates') }}
										/>
									)}
								</ListItem>
							)}
							{shouldRender([1, 2]) && (
								<ListItem
									button
									component={Link}
									to='/forms'
									selected={location.pathname === '/forms'}
									sx={getItemStyle('/forms')}
								>
									<ListItemIcon>
										<FileCopy style={getIconStyle('/forms')} />
									</ListItemIcon>
									{open && (
										<ListItemText
											primary='Forms'
											primaryTypographyProps={{ sx: getItemStyle('/forms') }}
										/>
									)}
								</ListItem>
							)}

							{shouldRender([1]) && (
								<ListItem
									button
									component={Link}
									to='/personas'
									selected={location.pathname === '/personas'}
									sx={getItemStyle('/personas')}
								>
									<ListItemIcon>
										<ListIcon style={getIconStyle('/personas')} />
									</ListItemIcon>
									{open && (
										<ListItemText
											primary='Personas'
											primaryTypographyProps={{ sx: getItemStyle('/personas') }}
										/>
									)}
								</ListItem>
							)}

							<ListItem
								button
								component={Link}
								to='/tasks'
								selected={location.pathname.startsWith('/tasks')}
								sx={getItemStyle('/tasks')}
							>
								<ListItemIcon>
									<Schedule style={getIconStyle('/tasks')} />
								</ListItemIcon>
								{open && (
									<ListItemText
										primary='Tasks'
										primaryTypographyProps={{ sx: getItemStyle('/tasks') }}
									/>
								)}
							</ListItem>

							<ListItem
								button
								component={Link}
								to='/learning-journals'
								selected={location.pathname.startsWith('/learning-journals')}
								sx={getItemStyle('/learning-journals')}>
								<ListItemIcon>
									<ListIcon style={getIconStyle('/learning-journals')} />
								</ListItemIcon>
								{open && (
									<ListItemText
										primary='Learning Journals'
										primaryTypographyProps={{ sx: getItemStyle('/learning-journals') }}
									/>
								)}
							</ListItem>

							{shouldRender([1, 4]) && (
								<ListItem
									button
									component={Link}
									to='/projects'
									selected={location.pathname === '/projects'}
									sx={getItemStyle('/projects')}
								>
									<ListItemIcon>
										<Description style={getIconStyle('/projects')} />
									</ListItemIcon>
									{open && (
										<ListItemText
											primary='Projects'
											primaryTypographyProps={{ sx: getItemStyle('/projects') }}
										/>
									)}
								</ListItem>
							)}
						</List>
					</Box>

					{/* Bottom Section - User Info (Picture & Name) */}
					<Box sx={{ paddingBottom: '10px' }} className='sideDrawerBottomBar'>
						<ListItem
							button
							onClick={handleMenuClick}
							sx={{ padding: '10px', color: 'rgba(0, 0, 0, 0.56)' }}
						>
							<ListItemIcon>
								{!user.AccountPictureUrl && (
									<AccountCircle style={{ fontSize: '24px', color: 'rgba(0, 0, 0, 0.56)' }} />
								)}
								{user.AccountPictureUrl && (
									<img
										className='userPhoto'
										src={user.AccountPictureUrl}
										alt='images'
										border='0'
									></img>
								)}
							</ListItemIcon>
							{open && <ListItemText primary={`${user.FirstName} ${user.LastName}`} />}
							{open && (
								<UnfoldMoreIcon
									style={{
										fontSize: '20px',
										color: 'rgba(0, 0, 0, 0.56)',
										transform: expandMenu ? 'rotate(180deg)' : 'rotate(0deg)',
										transition: 'transform 0.3s',
									}}
								/>
							)}
						</ListItem>

						<Collapse in={expandMenu && open} timeout='auto' unmountOnExit orientation='vertical'>
							<List component='div' disablePadding>
								<ListItem button component={Link} to='/settings'>
									<ListItemIcon>
										<Settings />
									</ListItemIcon>
									<ListItemText primary='Settings' />
								</ListItem>
								<ListItem button onClick={handleLogout} style={{ cursor: 'pointer' }}>
									<ListItemIcon>
										<Logout />
									</ListItemIcon>
									<ListItemText primary='Log out' />
								</ListItem>
							</List>
						</Collapse>
					</Box>
				</Drawer>
			</div>
		)
	);
};

export default Sidebar;
